import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api';
import type { SavedView, ViewPage } from '../types';
import { decodeView, describeRange, encodeView, isRolling, sameFilters } from '../views';

interface Props<T extends object> {
  page: ViewPage;
  filters: T;
  defaults: T;
  onApply: (filters: T) => void;
}

/*
 * Remembered across tab switches (the filter bars unmount), so the default
 * view is applied once per visit and the open view's name survives a tab change.
 */
const defaultApplied = new Set<ViewPage>();
const activeByPage = new Map<ViewPage, string>();

/**
 * The Views menu in a filter panel's header: open a saved view, save the
 * current filters as one, update or delete it, and pick the default the
 * page opens with.
 */
export function SavedViews<T extends object>({ page, filters, defaults, onApply }: Props<T>) {
  const [views, setViews] = useState<SavedView[]>([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [rolling, setRolling] = useState(true);
  const [makeDefault, setMakeDefault] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(activeByPage.get(page) ?? null);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .views(page)
      .then(({ views: list }) => {
        if (cancelled) return;
        setViews(list);
        if (!defaultApplied.has(page)) {
          defaultApplied.add(page);
          const preferred = list.find((view) => view.isDefault);
          if (preferred) {
            onApply(decodeView(preferred.filters, defaults));
            choose(preferred.id);
          }
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // Loads once per mount; onApply and defaults are stable enough for that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) close();
    };
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && close();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  function close() {
    setOpen(false);
    setSaving(false);
    setError(null);
  }

  function choose(id: string | null) {
    setActiveId(id);
    if (id) activeByPage.set(page, id);
    else activeByPage.delete(page);
  }

  const active = views.find((view) => view.id === activeId) ?? null;
  const modified = active ? !sameFilters(decodeView(active.filters, defaults), filters) : false;

  async function run(action: () => Promise<void>) {
    setError(null);
    try {
      await action();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
    }
  }

  function apply(view: SavedView) {
    onApply(decodeView(view.filters, defaults));
    choose(view.id);
    close();
  }

  async function saveNew() {
    await run(async () => {
      const { view } = await api.createView({
        page,
        name: name.trim(),
        filters: encodeView(filters, rolling),
        isDefault: makeDefault,
      });
      setViews((current) =>
        [...current.map((item) => (makeDefault ? { ...item, isDefault: false } : item)), view].sort((a, b) =>
          a.name.localeCompare(b.name),
        ),
      );
      choose(view.id);
      setName('');
      setMakeDefault(false);
      close();
    });
  }

  async function updateActive() {
    if (!active) return;
    await run(async () => {
      const { view } = await api.updateView(active.id, { filters: encodeView(filters, isRolling(active.filters)) });
      setViews((current) => current.map((item) => (item.id === view.id ? view : item)));
      close();
    });
  }

  async function toggleDefault(view: SavedView) {
    await run(async () => {
      const { view: updated } = await api.updateView(view.id, { isDefault: !view.isDefault });
      setViews((current) =>
        current.map((item) =>
          item.id === updated.id ? updated : updated.isDefault ? { ...item, isDefault: false } : item,
        ),
      );
    });
  }

  async function remove(view: SavedView) {
    if (!window.confirm(`Delete the view “${view.name}”?`)) return;
    await run(async () => {
      await api.deleteView(view.id);
      setViews((current) => current.filter((item) => item.id !== view.id));
      if (activeId === view.id) choose(null);
    });
  }

  return (
    <div className="views-menu" ref={root}>
      <button
        type="button"
        className={`btn btn-sm views-trigger${active ? ' has-view' : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
          <path d="M3 2.5h10v11l-5-3-5 3z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
        {active ? active.name : 'Views'}
        {modified ? <span className="views-modified" title="Filters changed since this view was opened">•</span> : null}
        <span aria-hidden="true" className="views-caret">▾</span>
      </button>

      {open ? (
        <div className="views-popover" role="menu">
          {error ? <div className="views-error">{error}</div> : null}
          {views.length ? (
            <ul className="views-list">
              {views.map((view) => (
                <li key={view.id} className={view.id === activeId ? 'current' : undefined}>
                  <button type="button" className="views-item" role="menuitem" onClick={() => apply(view)}>
                    <span className="views-name">{view.name}</span>
                    <span className="views-meta">
                      {describeRange(view.filters) ?? (isRolling(view.filters) ? 'rolling dates' : 'fixed dates')}
                      {view.isDefault ? ' · default' : ''}
                    </span>
                  </button>
                  <button
                    type="button"
                    className={`icon-button${view.isDefault ? ' on' : ''}`}
                    title={view.isDefault ? 'Stop opening the page with this view' : 'Open the page with this view'}
                    aria-label={view.isDefault ? `Unset ${view.name} as default` : `Set ${view.name} as default`}
                    onClick={() => toggleDefault(view)}
                  >
                    ★
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    title="Delete view"
                    aria-label={`Delete ${view.name}`}
                    onClick={() => remove(view)}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="views-empty">No saved views yet. Set the filters you use often, then save them here.</p>
          )}

          <div className="views-actions">
            {active && modified ? (
              <button type="button" className="btn btn-sm" onClick={updateActive}>
                Update “{active.name}”
              </button>
            ) : null}
            {active ? (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => {
                  choose(null);
                  onApply(defaults);
                  close();
                }}
              >
                Clear view
              </button>
            ) : null}
            {!saving ? (
              <button type="button" className="btn btn-sm btn-primary" onClick={() => setSaving(true)}>
                Save current filters…
              </button>
            ) : null}
          </div>

          {saving ? (
            <form
              className="views-form"
              onSubmit={(event) => {
                event.preventDefault();
                if (name.trim()) void saveNew();
              }}
            >
              <input
                type="text"
                autoFocus
                placeholder="View name, e.g. Hot leads this month"
                value={name}
                maxLength={80}
                onChange={(event) => setName(event.target.value)}
              />
              <label className="switch small">
                <input type="checkbox" role="switch" checked={rolling} onChange={(event) => setRolling(event.target.checked)} />
                Dates roll forward (keep “last 30 days” current)
              </label>
              <label className="switch small">
                <input type="checkbox" role="switch" checked={makeDefault} onChange={(event) => setMakeDefault(event.target.checked)} />
                Open this page with this view
              </label>
              <div className="row">
                <button type="submit" className="btn btn-sm btn-primary" disabled={!name.trim()}>
                  Save view
                </button>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setSaving(false)}>
                  Cancel
                </button>
              </div>
            </form>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
